// Tag/Woche/Monat/Jahr-Rollover-Logik für Todos
// Pure Funktion (manipuliert nur userStorage). Kann sicher mehrfach aufgerufen
// werden – idempotent: führt nur Änderungen durch, wenn sich der aktuelle Tag/
// die KW/der Monat geändert hat.
//
// Wird genutzt von:
// - components/calendar/TodosView.jsx (beim Mount, periodisch, visibilitychange)
// - pages/ZielplanungsKalender.js (beim Mount der Hauptkomponente, falls
//   TodosView nicht offen ist)
import { userStorage } from './userStorage';
import { getLocalDateStr, getWeekNumber } from './calendarUtils';
import { STORAGE_KEYS } from './calendarConstants';

/**
 * Führt das komplette Todo-Rollover aus (Daily → Weekly → Monthly → Yearly).
 * Schreibt direkt in userStorage. Gibt den finalen Snapshot zurück, damit
 * Aufrufer ihre State-Hooks aktualisieren können.
 *
 * @returns {{
 *   today: string,
 *   currentWeekKey: string,
 *   currentMonthKey: string,
 *   dailyToday: Array,
 *   weeklyCurrent: Array,
 *   monthlyCurrent: Array,
 *   yearly: Array,
 *   changed: boolean,
 *   monthlyChanged: boolean,
 *   yearlyChanged: boolean
 * }}
 */
export function runTodoRollover(now = new Date()) {
  const today = getLocalDateStr(now);
  const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1; // 0=Mo, 6=So
  const currentWeekNum = getWeekNumber(now);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentMonthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
  const currentWeekKey = `${currentYear}-W${currentWeekNum}`;

  const loadJSON = (key, def) => {
    try {
      const raw = userStorage.getItem(key);
      return raw ? JSON.parse(raw) : def;
    } catch {
      return def;
    }
  };

  const storedDaily = loadJSON(STORAGE_KEYS.dailyTodos, {});
  const storedWeekly = loadJSON(STORAGE_KEYS.weeklyTodos, {});
  let storedMonthly = loadJSON(STORAGE_KEYS.monthlyTodos, {});
  const storedYearly = loadJSON(STORAGE_KEYS.backlogTodos, []);

  // Handle old array format for monthly
  if (Array.isArray(storedMonthly)) {
    storedMonthly = { [currentMonthKey]: storedMonthly };
  }

  let hasChanges = false;
  let monthlyChanged = false;
  let yearlyChanged = false;

  // ─── 0. Erledigte Todos bei neuem Tag entfernen ─────────────────────────────
  // User-Wunsch: beim Tageswechsel werden ALLE abgehakten (done=true) Todos
  // aus Weekly (aktuelle KW), Monthly (aktueller Monat) und Yearly aufgeräumt.
  // Daily wird in Schritt 1 ohnehin pro-Tag komplett gelöscht.
  // Idempotent – bei wiederholtem Lauf am gleichen Tag keine Änderung mehr möglich,
  // weil erledigte Todos bereits weg sind.
  Object.keys(storedWeekly).forEach((k) => {
    const before = (storedWeekly[k] || []).length;
    const cleaned = (storedWeekly[k] || []).filter((t) => !t.done);
    if (cleaned.length !== before) {
      storedWeekly[k] = cleaned;
      hasChanges = true;
    }
  });
  Object.keys(storedMonthly).forEach((k) => {
    const before = (storedMonthly[k] || []).length;
    const cleaned = (storedMonthly[k] || []).filter((t) => !t.done);
    if (cleaned.length !== before) {
      storedMonthly[k] = cleaned;
      monthlyChanged = true;
    }
  });
  {
    const before = storedYearly.length;
    const cleanedY = storedYearly.filter((t) => !t.done);
    if (cleanedY.length !== before) {
      storedYearly.length = 0;
      cleanedY.forEach((t) => storedYearly.push(t));
      yearlyChanged = true;
    }
  }

  // ─── 1. Daily → Weekly: alte Tage (vor heute) → "Diese Woche" ───────────────
  Object.keys(storedDaily).forEach((date) => {
    if (date < today) {
      const oldTodos = storedDaily[date] || [];
      const uncompletedTodos = oldTodos.filter((t) => !t.done);
      if (uncompletedTodos.length > 0) {
        const wt = storedWeekly[currentWeekKey] || [];
        uncompletedTodos.forEach((todo) => {
          const { weekday, ...rest } = todo;
          wt.push({ ...rest, movedFromDaily: date });
        });
        storedWeekly[currentWeekKey] = wt;
        hasChanges = true;
      }
      delete storedDaily[date];
      hasChanges = true;
    }
  });

  // ─── 2. Weekly: cleanup vergangener Wochentage ──────────────────────────────
  // - weekday undefined/null → "Diese Woche" → behalten
  // - weekday >= todayWeekday → noch in der Zukunft (oder heute) → behalten
  // - weekday < todayWeekday + done → erledigt + Tag vorbei → löschen
  // - weekday < todayWeekday + !done → Tag verpasst → als "Diese Woche" (ohne weekday)
  //   neu hinzufügen, Original entfernen (sonst Duplikat)
  const weeklyTodos = storedWeekly[currentWeekKey] || [];
  const cleanedWeeklyTodos = weeklyTodos.filter((todo) => {
    if (todo.weekday === undefined || todo.weekday === null) return true;
    if (todo.weekday >= todayWeekday) return true;
    // weekday < todayWeekday → vergangener Tag → in jedem Fall aus dieser Liste entfernen
    return false;
  });

  const todosToMoveToFlexible = weeklyTodos.filter((todo) => {
    if (todo.weekday === undefined || todo.weekday === null) return false;
    if (todo.weekday >= todayWeekday) return false;
    return !todo.done;
  });

  todosToMoveToFlexible.forEach((todo) => {
    const { weekday, ...rest } = todo;
    cleanedWeeklyTodos.push({ ...rest, movedFromWeekday: weekday });
  });

  if (cleanedWeeklyTodos.length !== weeklyTodos.length || todosToMoveToFlexible.length > 0) {
    storedWeekly[currentWeekKey] = cleanedWeeklyTodos;
    hasChanges = true;
  }

  // ─── 3. Old weeks → Monthly "Diesen Monat" ──────────────────────────────────
  Object.keys(storedWeekly).forEach((weekKey) => {
    if (weekKey < currentWeekKey) {
      const oldWeekTodos = storedWeekly[weekKey] || [];
      const uncompletedTodos = oldWeekTodos.filter((t) => !t.done);
      if (uncompletedTodos.length > 0) {
        const mt = storedMonthly[currentMonthKey] || [];
        uncompletedTodos.forEach((todo) => {
          const { weekday, weekNum, ...rest } = todo;
          mt.push({ ...rest, movedFromWeek: weekKey });
        });
        storedMonthly[currentMonthKey] = mt;
        monthlyChanged = true;
      }
      delete storedWeekly[weekKey];
      hasChanges = true;
    }
  });

  // ─── 4. Monthly: vergangene KWs aufräumen ───────────────────────────────────
  // Gleiche Logik wie weekly (kein Duplikat-Bug):
  // - weekNum undefined/null → "Diesen Monat" → behalten
  // - weekNum >= currentWeekNum → behalten
  // - weekNum < currentWeekNum (egal ob done) → aus dieser Liste entfernen,
  //   uncompleted werden gleich als "Diesen Monat" o. nächste KW neu gepushed
  const currentMonthTodos = storedMonthly[currentMonthKey] || [];
  const cleanedMonthTodos = currentMonthTodos.filter((todo) => {
    if (todo.weekNum === undefined || todo.weekNum === null) return true;
    if (todo.weekNum >= currentWeekNum) return true;
    return false;
  });

  const monthTodosToMove = currentMonthTodos.filter((todo) => {
    if (todo.weekNum === undefined || todo.weekNum === null) return false;
    if (todo.weekNum >= currentWeekNum) return false;
    return !todo.done;
  });

  monthTodosToMove.forEach((todo) => {
    const { weekNum, ...rest } = todo;
    const nextKW = weekNum + 1;
    if (nextKW <= currentWeekNum) {
      cleanedMonthTodos.push({
        ...rest,
        weekNum: nextKW <= currentWeekNum ? currentWeekNum : nextKW,
        movedFromKW: weekNum,
      });
    } else {
      cleanedMonthTodos.push({ ...rest, movedFromKW: weekNum });
    }
  });

  if (cleanedMonthTodos.length !== currentMonthTodos.length || monthTodosToMove.length > 0) {
    storedMonthly[currentMonthKey] = cleanedMonthTodos;
    monthlyChanged = true;
  }

  // ─── 5. Old months → current month "Diesen Monat" ───────────────────────────
  Object.keys(storedMonthly).forEach((monthKey) => {
    if (monthKey < currentMonthKey) {
      const oldMonthTodos = storedMonthly[monthKey] || [];
      const uncompletedTodos = oldMonthTodos.filter((t) => !t.done);
      if (uncompletedTodos.length > 0) {
        const ct = storedMonthly[currentMonthKey] || [];
        uncompletedTodos.forEach((todo) => {
          const { weekNum, ...rest } = todo;
          ct.push({ ...rest, movedFromMonth: monthKey });
        });
        storedMonthly[currentMonthKey] = ct;
        monthlyChanged = true;
      }
      delete storedMonthly[monthKey];
      monthlyChanged = true;
    }
  });

  // ─── 6. Yearly: past-month uncompleted → current month ──────────────────────
  const cleanedYearly = [];
  let yearlyMoved = false;
  storedYearly.forEach((todo) => {
    if (todo.monthIndex !== undefined && todo.monthIndex !== null && todo.monthIndex < currentMonth) {
      if (!todo.done) {
        const ct = storedMonthly[currentMonthKey] || [];
        const { monthIndex, ...rest } = todo;
        ct.push({ ...rest, movedFromYearMonth: monthIndex });
        storedMonthly[currentMonthKey] = ct;
        monthlyChanged = true;
        yearlyMoved = true;
      }
      yearlyMoved = true;
    } else {
      cleanedYearly.push(todo);
    }
  });

  if (yearlyMoved) {
    storedYearly.length = 0;
    cleanedYearly.forEach((t) => storedYearly.push(t));
    yearlyChanged = true;
  }

  // ─── 7. Today's weekly Todos (weekday match) → Daily ────────────────────────
  const currentWeeklyTodos = storedWeekly[currentWeekKey] || [];
  const todaysWeeklyTodos = currentWeeklyTodos.filter((t) => t.weekday === todayWeekday && !t.done);
  const remainingWeeklyTodos = currentWeeklyTodos.filter((t) => t.weekday !== todayWeekday || t.done);

  if (todaysWeeklyTodos.length > 0) {
    const todayTodos = storedDaily[today] || [];
    todaysWeeklyTodos.forEach((todo) => {
      if (!todayTodos.find((t) => t.id === todo.id)) {
        todayTodos.push({ ...todo, movedFromWeekly: true });
      }
    });
    storedDaily[today] = todayTodos;
    storedWeekly[currentWeekKey] = remainingWeeklyTodos;
    hasChanges = true;
  }

  // ─── Persistieren ───────────────────────────────────────────────────────────
  // Vor dem Speichern alle Listen nach Priorität sortieren (high → low),
  // damit übergebene Todos sich korrekt zwischen bestehende einreihen.
  // Konsistent mit dem Add-Verhalten in TodosView.jsx.
  const sortByPriorityDesc = (list) =>
    Array.isArray(list)
      ? [...list].sort((a, b) => (b.priority || 0) - (a.priority || 0))
      : list;

  if (hasChanges || monthlyChanged || yearlyChanged) {
    // Daily: pro-Datum-Listen sortieren
    Object.keys(storedDaily).forEach((d) => {
      storedDaily[d] = sortByPriorityDesc(storedDaily[d]);
    });
    // Weekly: pro-Wochen-Listen sortieren
    Object.keys(storedWeekly).forEach((w) => {
      storedWeekly[w] = sortByPriorityDesc(storedWeekly[w]);
    });
    // Monthly: pro-Monat-Listen sortieren
    Object.keys(storedMonthly).forEach((m) => {
      storedMonthly[m] = sortByPriorityDesc(storedMonthly[m]);
    });
    // Yearly: flaches Array
    const sortedYearly = sortByPriorityDesc(storedYearly);
    storedYearly.length = 0;
    sortedYearly.forEach((t) => storedYearly.push(t));

    userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(storedDaily));
    userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(storedWeekly));
    if (monthlyChanged) {
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(storedMonthly));
    }
    if (yearlyChanged) {
      userStorage.setItem(STORAGE_KEYS.backlogTodos, JSON.stringify(storedYearly));
    }
  }

  return {
    today,
    currentWeekKey,
    currentMonthKey,
    dailyToday: storedDaily[today] || [],
    weeklyCurrent: storedWeekly[currentWeekKey] || [],
    monthlyCurrent: storedMonthly[currentMonthKey] || [],
    yearly: storedYearly,
    changed: hasChanges,
    monthlyChanged,
    yearlyChanged,
  };
}
